import React, { createContext, useContext, useState, useCallback } from 'react';

// Context para manejo global de errores
const ErrorContext = createContext();

// Hook para usar el contexto de errores
export const useErrorHandler = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useErrorHandler must be used within an ErrorProvider');
  }
  return context;
};

// Componente de Error Boundary
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 max-w-2xl mx-auto mt-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h2 className="text-xl font-semibold text-red-600">
                Something went wrong
              </h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={this.handleRetry}
                className="btn-primary flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Provider de manejo de errores
export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);

  const addError = useCallback((error, options = {}) => {
    const errorId = Date.now() + Math.random();
    const newError = {
      id: errorId,
      error,
      timestamp: new Date(),
      ...options
    };
    
    setErrors(prev => [...prev, newError]);
    
    // Auto-remove error after 10 seconds
    setTimeout(() => {
      removeError(errorId);
    }, 10000);
    
    return errorId;
  }, []);

  const removeError = useCallback((errorId) => {
    setErrors(prev => prev.filter(err => err.id !== errorId));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  const handleApiError = useCallback((error, context = '') => {
    // Determinar el tipo de error
    let errorMessage = 'An unexpected error occurred';
    let severity = 'error';
    
    if (error?.response?.status === 401) {
      errorMessage = 'Session expired. Please log in again.';
      severity = 'warning';
    } else if (error?.response?.status === 403) {
      errorMessage = 'You do not have permission to perform this action.';
      severity = 'warning';
    } else if (error?.response?.status === 404) {
      errorMessage = 'The requested resource was not found.';
      severity = 'info';
    } else if (error?.response?.status >= 500) {
      errorMessage = 'Server error. Please try again later.';
      severity = 'error';
    } else if (error?.code === 'ECONNABORTED') {
      errorMessage = 'Request timeout. Please check your connection.';
      severity = 'warning';
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    return addError(errorMessage, { severity, context });
  }, [addError]);

  const value = {
    errors,
    addError,
    removeError,
    clearErrors,
    handleApiError
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

export default ErrorProvider;